import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { 
  Plus, Download, Eye, Trash2, Calendar, Clock, 
  Upload, Loader2, ChevronDown, ChevronUp, File, Paperclip,
  Pencil, Save, X, ArrowUpDown, ArrowUp, ArrowDown, FileText, Search,
  Sparkles, FileOutput, PenTool
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import { adjuntosAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';
import NuevaActuacionIA from './NuevaActuacionIA';
import EnviarFirmaDialog from './EnviarFirmaDialog';
import { FirmaBadge, useFirmasExpediente } from './FirmasEstado';

// Formatear tamaño de archivo
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// v105: distintivo de las actuaciones escritas por la IA / el auditor
const TIPOS_IA_V105 = ['análisis ia', 'analisis ia', 'auditoría ia', 'auditoria ia', 'informe_ia'];

const PRIORIDAD_ESTILO_V105 = {
  'Urgente': 'bg-red-100 text-red-800 border-red-200',
  'Acción del despacho': 'bg-amber-100 text-amber-900 border-amber-200',
  'Esperando a terceros': 'bg-blue-100 text-blue-800 border-blue-200',
  'Sin acción': 'bg-gray-100 text-gray-700 border-gray-200',
};

const prioridadActuacionV105 = (act) => {
  const cuerpo = `${act?.descripcion || ''}\n${act?.observaciones || ''}`;
  const m = cuerpo.match(/\*\*Prioridad:?\*\*\s*([^\n·|*]+)/);
  if (!m) return null;
  const t = m[1].normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (t.includes('urgent')) return 'Urgente';
  if (t.includes('despacho')) return 'Acción del despacho';
  if (t.includes('tercero')) return 'Esperando a terceros';
  if (t.includes('sin accion')) return 'Sin acción';
  return null;
};

const ActuacionIABadges = ({ act }) => {
  const tipo = (act?.tipo || '').toLowerCase();
  if (!TIPOS_IA_V105.includes(tipo)) return null;
  const prioridad = prioridadActuacionV105(act);
  return (
    <>
      <Badge
        className="text-xs bg-purple-100 text-purple-800 border border-purple-200 hover:bg-purple-100"
        data-testid={`actuacion-badge-ia-${act.id}`}
      >
        <Sparkles className="w-3 h-3 mr-1" />
        {tipo.startsWith('audit') ? 'Auditoría IA' : 'Análisis IA'}
      </Badge>
      {prioridad && (
        <Badge
          className={`text-xs border ${PRIORIDAD_ESTILO_V105[prioridad]} hover:opacity-100`}
          data-testid={`actuacion-badge-prioridad-${act.id}`}
        >
          {prioridad}
        </Badge>
      )}
    </>
  );
};

// Componente para mostrar adjuntos de una actuación
const ActuacionAdjuntos = ({ actuacionId, adjuntos, onUpdate, firmasPorAdjunto = {}, onFirmasChange }) => {
  const [uploading, setUploading] = useState(false);
  const [seleccionados, setSeleccionados] = useState([]);
  const [descargandoZip, setDescargandoZip] = useState(false);

  const toggleSeleccion = (id) => {
    setSeleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleDownloadZip = async () => {
    setDescargandoZip(true);
    try {
      const response = await adjuntosAPI.downloadZipActuacion(actuacionId, seleccionados);
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', match ? match[1] : `actuacion-${actuacionId}_adjuntos.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      const omitidos = parseInt(response.headers?.['x-adjuntos-omitidos'] || '0', 10);
      toast({
        title: 'ZIP descargado',
        description: omitidos > 0 ? `${omitidos} archivo(s) no estaban disponibles en el almacén` : undefined,
      });
      setSeleccionados([]);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo generar el ZIP', variant: 'destructive' });
    }
    setDescargandoZip(false);
  };
  const [convertingId, setConvertingId] = useState(null);
  const [ocrProgreso, setOcrProgreso] = useState(null);
  const [convertingPdfId, setConvertingPdfId] = useState(null);
  const [firmaAdjunto, setFirmaAdjunto] = useState(null);
  const fileInputRef = useRef(null);

  const isPdfAdjunto = (adj) => (adj?.nombre || '').toLowerCase().endsWith('.pdf') || adj?.tipo_archivo === 'application/pdf';

  const isWordAdjunto = (adj) => {
    const nombre = (adj?.nombre || '').toLowerCase();
    const mime = (adj?.tipo_archivo || '').toLowerCase();
    return ['.docx', '.doc', '.rtf', '.odt'].some((ext) => nombre.endsWith(ext))
      || mime.includes('word') || mime.includes('opendocument.text');
  };

  const handleConvertPdf = async (adj) => {
    setConvertingPdfId(adj.id);
    try {
      await adjuntosAPI.convertirPdf(adj.id);
      toast({ title: 'PDF generado', description: 'El PDF se ha añadido a la actuación' });
      onUpdate();
    } catch (error) {
      const status = error?.response?.status;
      const msg = error?.response?.data?.detail || 'No se pudo convertir a PDF';
      toast({
        title: status === 409 ? 'El PDF ya existe' : 'Error',
        description: msg,
        variant: status === 409 ? 'default' : 'destructive',
      });
    } finally {
      setConvertingPdfId(null);
    }
  };

  const handleConvertMarkdown = async (adj) => {
    setConvertingId(adj.id);
    setOcrProgreso(null);
    try {
      const res = await adjuntosAPI.convertirMarkdown(adj.id);

      // 202: PDF escaneado largo → se procesa en cola, de uno en uno
      if (res?.status === 202 && res?.data?.job_id) {
        const { job_id, paginas_total } = res.data;
        setOcrProgreso({ adjuntoId: adj.id, hechas: 0, total: paginas_total, estado: 'en_cola' });
        toast({
          title: `OCR en cola (${paginas_total} páginas)`,
          description: 'Se procesa de uno en uno para no ralentizar la aplicación. Verás el progreso aquí mismo.',
        });
        await seguirOcrJob(job_id, adj);
        return;
      }

      const ocr = res?.data?.ocr_used;
      toast({ title: 'Markdown generado', description: ocr ? 'Transcripción por OCR añadida (.md)' : 'Archivo .md añadido a la actuación' });
      onUpdate();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'No se pudo convertir a Markdown';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setConvertingId(null);
    }
  };

  // Sigue el progreso de una conversión OCR encolada hasta que termina
  const seguirOcrJob = async (jobId, adj) => {
    const esperar = (ms) => new Promise((r) => setTimeout(r, ms));
    for (let intento = 0; intento < 1200; intento++) {
      await esperar(2500);
      let job;
      try {
        job = (await adjuntosAPI.ocrJobEstado(jobId))?.data;
      } catch {
        continue;
      }
      setOcrProgreso({
        adjuntoId: adj.id,
        hechas: job.paginas_hechas || 0,
        total: job.paginas_total || 0,
        estado: job.estado,
      });
      if (job.estado === 'completado') {
        toast({ title: 'Markdown generado', description: job.mensaje || 'Transcripción por OCR añadida (.md)' });
        setOcrProgreso(null);
        onUpdate();
        return;
      }
      if (job.estado === 'error') {
        toast({ title: 'La conversión falló', description: job.mensaje || 'Error en el OCR', variant: 'destructive' });
        setOcrProgreso(null);
        return;
      }
    }
    setOcrProgreso(null);
    toast({ title: 'OCR muy lento', description: 'La conversión sigue en marcha; refresca en unos minutos.' });
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo_adjunto', 'documento');
      
      try {
        await adjuntosAPI.uploadActuacion(actuacionId, formData);
        successCount++;
      } catch (error) {
        console.error('Error subiendo archivo:', error);
        toast({
          title: "Error",
          description: `No se pudo subir ${file.name}`,
          variant: "destructive"
        });
      }
    }
    
    if (successCount > 0) {
      toast({ title: `${successCount} archivo(s) subido(s)` });
      onUpdate();
    }
    setUploading(false);
  };

  const handleDownload = async (adjunto) => {
    try {
      const urlResponse = await adjuntosAPI.getUrl(adjunto.id);
      const { url, storage, nombre } = urlResponse.data || {};
      if (!url) {
        toast({ title: "Error", description: "No se pudo obtener URL", variant: "destructive" });
        return;
      }

      if (storage === 's3') {
        // URL firmada de S3: abrir directamente.
        window.open(url, "_blank");
      } else {
        // Local: fetch con Bearer y descarga por Blob (window.open perdería el token).
        const token = localStorage.getItem('lexduo_token');
        const absoluteUrl = url.startsWith('http')
          ? url
          : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        const r = await fetch(absoluteUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          throw new Error(`HTTP ${r.status}: ${txt}`);
        }
        const blob = await r.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = nombre || adjunto.nombre || 'archivo';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Error descargando adjunto:', error);
      toast({ title: "Error", description: "No se pudo descargar", variant: "destructive" });
    }
  };

  const handleDownloadOLD = async (adjunto) => {
    // Abrir directamente la URL de descarga - evita problemas de CORS con redirects
    const token = localStorage.getItem("lexduo_token");
    const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/adjuntos/download/${adjunto.id}`;
    window.open(downloadUrl, "_blank");
    return;
    // Código antiguo comentado:
    try {
      const response = await adjuntosAPI.download(adjunto.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', adjunto.nombre);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Error", description: "No se pudo descargar", variant: "destructive" });
    }
  };

  const handleDelete = async (adjuntoId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await adjuntosAPI.delete(adjuntoId);
      toast({ title: "Archivo eliminado" });
      onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  if (!adjuntos || adjuntos.length === 0) {
    return (
      <div className="mt-3 pt-3 border-t">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <Paperclip className="w-4 h-4" /> Sin adjuntos
          </span>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple
            onChange={(e) => handleUpload(Array.from(e.target.files || []))}
          />
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Adjuntar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
          <Paperclip className="w-4 h-4" /> Adjuntos ({adjuntos.length})
        </span>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple
          onChange={(e) => handleUpload(Array.from(e.target.files || []))}
        />
        <div className="flex items-center gap-2">
          {adjuntos.length > 1 && (
            <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                data-testid={`chk-seleccionar-todos-${actuacionId}`}
                checked={seleccionados.length === adjuntos.length}
                onChange={(e) => setSeleccionados(e.target.checked ? adjuntos.map((a) => a.id) : [])}
              />
              Todos
            </label>
          )}
          <Button
            size="sm"
            variant="outline"
            data-testid={`btn-descargar-zip-${actuacionId}`}
            onClick={handleDownloadZip}
            disabled={descargandoZip}
            title="Descargar los adjuntos en un único archivo ZIP"
          >
            {descargandoZip
              ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
              : <Download className="w-4 h-4 mr-1" />}
            {seleccionados.length > 0 ? `ZIP (${seleccionados.length})` : 'Descargar ZIP'}
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      <div className="space-y-1">
        {adjuntos.map((adj) => (
          <div key={adj.id} className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-sm">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                type="checkbox"
                data-testid={`chk-adjunto-${adj.id}`}
                className="flex-shrink-0"
                checked={seleccionados.includes(adj.id)}
                onChange={() => toggleSeleccion(adj.id)}
              />
              <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <button
                onClick={() => window.open(`/preview/${adj.id}`, '_blank')}
                className="text-blue-600 hover:underline truncate"
                title={adj.nombre}
              >
                {adj.nombre}
              </button>
              <span className="text-gray-400 text-xs flex-shrink-0">
                ({formatFileSize(adj.tamano)})
              </span>
              <FirmaBadge firma={firmasPorAdjunto[adj.id]} onRenovado={onFirmasChange} />
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0"
                onClick={() => window.open(`/preview/${adj.id}`, '_blank')}
                title="Previsualizar"
              >
                <Eye className="w-4 h-4 text-green-600" />
              </Button>
              {isPdfAdjunto(adj) && (
                <>
                {ocrProgreso?.adjuntoId === adj.id && (
                  <span
                    className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 whitespace-nowrap"
                    data-testid={`ocr-progreso-${adj.id}`}
                  >
                    {ocrProgreso.estado === 'en_cola'
                      ? `OCR en cola · ${ocrProgreso.total} pág.`
                      : `OCR ${ocrProgreso.hechas}/${ocrProgreso.total}`}
                  </span>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0"
                  onClick={() => handleConvertMarkdown(adj)}
                  disabled={convertingId === adj.id || ocrProgreso?.adjuntoId === adj.id}
                  title="Convertir a Markdown (OCR)"
                  data-testid={`convertir-md-btn-${adj.id}`}
                >
                  {convertingId === adj.id || ocrProgreso?.adjuntoId === adj.id ? <Loader2 className="w-4 h-4 animate-spin text-amber-600" /> : <FileText className="w-4 h-4 text-amber-600" />}
                </Button>
                </>
              )}
              {isWordAdjunto(adj) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0"
                  onClick={() => handleConvertPdf(adj)}
                  disabled={convertingPdfId === adj.id}
                  title="Convertir a PDF y adjuntar"
                  data-testid={`convertir-pdf-btn-${adj.id}`}
                >
                  {convertingPdfId === adj.id ? <Loader2 className="w-4 h-4 animate-spin text-red-600" /> : <FileOutput className="w-4 h-4 text-red-600" />}
                </Button>
              )}
              {(isPdfAdjunto(adj) || isWordAdjunto(adj)) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setFirmaAdjunto(adj)}
                  title="Enviar al cliente para que lo firme"
                  data-testid={`enviar-firma-btn-${adj.id}`}
                >
                  <PenTool className="w-4 h-4 text-indigo-600" />
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0"
                onClick={() => handleDownload(adj)}
                title="Descargar"
              >
                <Download className="w-4 h-4 text-blue-600" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 w-7 p-0"
                onClick={() => handleDelete(adj.id)}
                title="Eliminar"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <EnviarFirmaDialog
        adjunto={firmaAdjunto}
        open={!!firmaAdjunto}
        onOpenChange={(v) => { if (!v) setFirmaAdjunto(null); }}
        onDone={onUpdate}
      />
    </div>
  );
};

// Componente principal de Actuaciones
const ActuacionesSection = ({ expedienteId, actuaciones, onCreateActuacion, onRefresh, isDialogOpenExternal, onDialogOpenChange }) => {
  const [isDialogOpenInternal, setIsDialogOpenInternal] = useState(false);
  const isDialogOpen = isDialogOpenExternal !== undefined ? isDialogOpenExternal : isDialogOpenInternal;
  const setIsDialogOpen = onDialogOpenChange || setIsDialogOpenInternal;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isIADialogOpen, setIsIADialogOpen] = useState(false);
  const [editingActuacion, setEditingActuacion] = useState(null);
  const [observaciones, setObservaciones] = useState('');
  const [editObservaciones, setEditObservaciones] = useState('');
  const [adjuntosTemp, setAdjuntosTemp] = useState([]);
  const [editAdjuntosTemp, setEditAdjuntosTemp] = useState([]);
  const [saving, setSaving] = useState(false);
  const [actuacionesConAdjuntos, setActuacionesConAdjuntos] = useState({});
  const [adjuntosCounts, setAdjuntosCounts] = useState({});
  const [expandedActuaciones, setExpandedActuaciones] = useState(new Set());
  const firmasExp = useFirmasExpediente(expedienteId);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  
  // Estado para ordenación
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'desc' });
  
  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState('');

  // Función para cambiar el ordenamiento
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Obtener icono de ordenación
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" /> 
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  // Actuaciones ordenadas
  const sortedActuaciones = useMemo(() => {
    if (!actuaciones || actuaciones.length === 0) return [];
    
    // Primero filtrar por término de búsqueda
    let filtered = actuaciones;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = actuaciones.filter(act => {
        const titulo = (act.titulo || '').toLowerCase();
        const descripcion = (act.descripcion || '').toLowerCase();
        const observaciones = (act.observaciones || '').toLowerCase();
        const fecha = act.fecha ? new Date(act.fecha).toLocaleDateString('es-ES') : '';
        return titulo.includes(term) || descripcion.includes(term) || observaciones.includes(term) || fecha.includes(term);
      });
    }
    
    // Luego ordenar
    return [...filtered].sort((a, b) => {
      const { key, direction } = sortConfig;
      let comparison = 0;
      if (key === 'fecha') {
        const dateA = new Date(a.fecha || 0);
        const dateB = new Date(b.fecha || 0);
        comparison = dateA - dateB; if (comparison === 0) { const createdA = new Date(a.created_at || 0); const createdB = new Date(b.created_at || 0); comparison = createdA - createdB; }
      } else if (key === 'titulo') {
        const titleA = (a.titulo || '').toLowerCase();
        const titleB = (b.titulo || '').toLowerCase();
        comparison = titleA.localeCompare(titleB, 'es');
      }
      return direction === 'asc' ? comparison : -comparison;
    });
  }, [actuaciones, sortConfig, searchTerm]);

  // Cargar SOLO el conteo de adjuntos por actuación (1 request batch).
  // Los detalles se cargan on-demand al editar/expandir una actuación concreta.
  const loadAdjuntosActuaciones = useCallback(async () => {
    if (!actuaciones || actuaciones.length === 0) {
      setAdjuntosCounts({});
      return;
    }
    try {
      const ids = actuaciones.map(a => a.id).filter(Boolean);
      const chunkSize = 400;
      const merged = {};
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const res = await adjuntosAPI.getCountsActuaciones(chunk);
        Object.assign(merged, res.data || {});
      }
      const counts = {};
      for (const id of ids) {
        counts[id] = merged[String(id)] ?? merged[id] ?? 0;
      }
      setAdjuntosCounts(counts);
    } catch (error) {
      console.error('Error cargando contadores de adjuntos:', error);
      setAdjuntosCounts({});
    }
  }, [actuaciones]);

  // Carga los adjuntos detallados de una actuación concreta (al editar/expandir)
  const loadAdjuntosDetalle = useCallback(async (actuacionId) => {
    try {
      const response = await adjuntosAPI.getByActuacion(actuacionId);
      const items = response.data || [];
      setActuacionesConAdjuntos(prev => ({ ...prev, [actuacionId]: items }));
      setAdjuntosCounts(prev => ({ ...prev, [actuacionId]: items.length }));
    } catch (error) {
      console.error('Error cargando adjuntos de actuación:', error);
    }
  }, []);

  useEffect(() => {
    if (actuaciones.length > 0) {
      loadAdjuntosActuaciones();
    }
  }, [actuaciones, loadAdjuntosActuaciones]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const formData = new FormData(e.target);
    const data = {
      titulo: formData.get('titulo') || 'Sin título',
      fecha: new Date(formData.get('fecha')).toISOString(),
      tipo: 'general',
      descripcion: observaciones || 'Sin descripción',
      observaciones: observaciones
    };
    
    try {
      const newActuacion = await onCreateActuacion(data);
      
      if (adjuntosTemp.length > 0 && newActuacion?.id) {
        for (const file of adjuntosTemp) {
          const uploadData = new FormData();
          uploadData.append('file', file);
          uploadData.append('tipo_adjunto', 'documento');
          try {
            await adjuntosAPI.uploadActuacion(newActuacion.id, uploadData);
          } catch (err) {
            console.error('Error subiendo adjunto:', err);
          }
        }
      }
      
      setIsDialogOpen(false);
      setObservaciones('');
      setAdjuntosTemp([]);
      onRefresh();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo crear la actuación", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Editar actuación
  const handleEditClick = (act, e) => {
    e.stopPropagation();
    setEditingActuacion(act);
    setEditObservaciones(act.observaciones || act.descripcion || '');
    setIsEditDialogOpen(true);
    // Cargar adjuntos detallados de esta actuación (lazy)
    loadAdjuntosDetalle(act.id);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const formData = new FormData(e.target);
    const data = {
      titulo: formData.get('titulo') || editingActuacion.titulo,
      tipo: editingActuacion.tipo || 'general',
      descripcion: editObservaciones || editingActuacion.descripcion,
      observaciones: editObservaciones,
      fecha: new Date(formData.get('fecha')).toISOString()
    };
    
    try {
      await expedientesAPI.updateActuacion(editingActuacion.id, data);
      
      if (editAdjuntosTemp.length > 0) {
        for (const file of editAdjuntosTemp) {
          const uploadData = new FormData();
          uploadData.append("file", file);
          uploadData.append("tipo_adjunto", "documento");
          try {
            await adjuntosAPI.uploadActuacion(editingActuacion.id, uploadData);
          } catch (err) {
            console.error("Error subiendo adjunto:", err);
          }
        }
      }
      setEditAdjuntosTemp([]);
      toast({ title: "Actuación actualizada" });
      setIsEditDialogOpen(false);
      setEditingActuacion(null);
      setEditObservaciones('');
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
      toast({ title: "Error", description: "No se pudo actualizar la actuación", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Eliminar actuación
  const handleDeleteClick = async (act, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar la actuación "${act.titulo}"?`)) return;
    
    try {
      await expedientesAPI.deleteActuacion(act.id);
      toast({ title: "Actuación eliminada" });
      onRefresh();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar la actuación", variant: "destructive" });
    }
  };

  const handleAddTempFiles = (files) => {
    setAdjuntosTemp(prev => [...prev, ...Array.from(files)]);
  };

  const handleRemoveTempFile = (index) => {
    setAdjuntosTemp(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddEditTempFiles = (files) => {
    setEditAdjuntosTemp(prev => [...prev, ...Array.from(files)]);
  };

  const handleRemoveEditTempFile = (index) => {
    setEditAdjuntosTemp(prev => prev.filter((_, i) => i !== index));
  };

  const toggleActuacion = (id) => {
    setExpandedActuaciones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        // Lazy-load: solo cargar detalles cuando se expande
        if (!actuacionesConAdjuntos[id] && (adjuntosCounts[id] || 0) > 0) {
          loadAdjuntosDetalle(id);
        }
      }
      return newSet;
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-4">
        {/* Buscador de actuaciones */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar en actuaciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchTerm && (
          <span className="text-sm text-gray-500">
            {sortedActuaciones.length} de {actuaciones.length} actuaciones
          </span>
        )}
        {/* Dialog Nueva Actuación */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => setIsIADialogOpen(true)}
            data-testid="nueva-actuacion-ia-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />ACTUACIÓN IA
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-blue-700 hover:bg-blue-800">
              <Plus className="w-4 h-4 mr-2" />NUEVA ACTUACIÓN
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nueva actuación</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Label>Título *</Label>
                  <Input name="titulo" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha</Label>
                    <Input name="fecha" type="date" defaultValue={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div>
                    <Label>Duración</Label>
                    <Input name="duracion" type="time" step="1" />
                  </div>
                </div>
                
                <div data-color-mode="light">
                  <Label>Observaciones</Label>
                  <div className="mt-1">
                    <MDEditor
                      value={observaciones}
                      onChange={setObservaciones}
                      preview="edit"
                      height={200}
                      textareaProps={{
                        placeholder: 'Escribe las observaciones... (soporta formato Markdown)'
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" /> Archivos adjuntos
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    multiple
                    onChange={(e) => handleAddTempFiles(e.target.files)}
                  />
                  <div 
                    className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                      const files = e.dataTransfer.files;
                      if (files && files.length > 0) {
                        handleAddTempFiles(files);
                      }
                    }}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Arrastra archivos aquí o haz clic para seleccionar</p>
                    <p className="text-xs text-gray-400 mt-1">Puedes seleccionar múltiples archivos</p>
                  </div>
                  
                  {adjuntosTemp.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Archivos a adjuntar:</p>
                      {adjuntosTemp.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1 px-2 bg-gray-100 rounded text-sm">
                          <div className="flex items-center gap-2">
                            <File className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{file.name}</span>
                            <span className="text-gray-400 text-xs">({formatFileSize(file.size)})</span>
                          </div>
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => handleRemoveTempFile(idx)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => {
                  setIsDialogOpen(false);
                  setObservaciones('');
                  setAdjuntosTemp([]);
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Editar Actuación */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar actuación</DialogTitle>
            </DialogHeader>
            {editingActuacion && (
              <form onSubmit={handleEditSubmit}>
                <div className="space-y-4">
                  <div>
                    <Label>Título *</Label>
                    <Input name="titulo" defaultValue={editingActuacion.titulo} required />
                  </div>
                  <div>
                    <Label>Fecha</Label>
                    <Input 
                      name="fecha" 
                      type="date" 
                      defaultValue={editingActuacion.fecha?.split('T')[0] || ''} 
                    />
                  </div>
                  
                  <div data-color-mode="light">
                    <Label>Observaciones</Label>
                    <div className="mt-1">
                      <MDEditor
                        value={editObservaciones}
                        onChange={setEditObservaciones}
                        preview="edit"
                        height={200}
                      />
                    </div>
                  </div>
                  
                  {/* Sección de adjuntos */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4" /> Archivos adjuntos
                    </Label>
                    <input
                      type="file"
                      ref={editFileInputRef}
                      className="hidden"
                      multiple
                      onChange={(e) => handleAddEditTempFiles(e.target.files)}
                    />
                    
                    {/* Adjuntos existentes */}
                    {actuacionesConAdjuntos[editingActuacion.id]?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-600">Adjuntos actuales:</p>
                        {actuacionesConAdjuntos[editingActuacion.id].map((adj) => (
                          <div key={adj.id} className="flex items-center justify-between py-1 px-2 bg-gray-100 rounded text-sm">
                            <div className="flex items-center gap-2">
                              <File className="w-4 h-4 text-gray-400" />
                              <span className="truncate">{adj.nombre}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => window.open("/preview/" + adj.id, "_blank")}>
                              <Eye className="w-4 h-4 text-green-600" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Zona para añadir nuevos */}
                    <div
                      className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50"
                      onClick={() => editFileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); if (e.dataTransfer.files?.length > 0) handleAddEditTempFiles(e.dataTransfer.files); }}
                    >
                      <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                      <p className="text-sm text-gray-600">Arrastrar archivos aquí o clic para seleccionar</p>
                    </div>
                    
                    {/* Nuevos archivos pendientes */}
                    {editAdjuntosTemp.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-sm font-medium text-gray-700">Nuevos archivos:</p>
                        {editAdjuntosTemp.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between py-1 px-2 bg-blue-50 rounded text-sm">
                            <div className="flex items-center gap-2">
                              <File className="w-4 h-4 text-blue-400" />
                              <span className="truncate">{file.name}</span>
                            </div>
                            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleRemoveEditTempFile(idx)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => {
                    setIsEditDialogOpen(false);
                    setEditingActuacion(null);
                    setEditObservaciones('');
                    setEditAdjuntosTemp([]);
                  }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Guardar cambios
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
        </div>

        {/* Dialog IA Actuación */}
        <NuevaActuacionIA
          expedienteId={expedienteId}
          open={isIADialogOpen}
          onOpenChange={setIsIADialogOpen}
          onSuccess={() => onRefresh()}
        />
      </div>
      
      {/* Lista de actuaciones */}
      <div className="space-y-3">
        {/* Controles de ordenación */}
        {actuaciones.length > 0 && (
          <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
            <span className="text-sm font-medium text-gray-700">Ordenar por:</span>
            <button
              onClick={() => handleSort('fecha')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                sortConfig.key === 'fecha' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Fecha
              {getSortIcon('fecha')}
            </button>
            <button
              onClick={() => handleSort('titulo')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                sortConfig.key === 'titulo' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <FileText className="w-4 h-4" />
              Título
              {getSortIcon('titulo')}
            </button>
            <span className="ml-auto text-xs text-gray-500">
              {actuaciones.length} actuación{actuaciones.length !== 1 ? 'es' : ''}
            </span>
          </div>
        )}
        
        {sortedActuaciones.length === 0 ? (
          <p className="text-center text-gray-500 py-4">No hay actuaciones registradas</p>
        ) : (
          sortedActuaciones.map((act) => {
            const isExpanded = expandedActuaciones.has(act.id);
            const adjuntos = actuacionesConAdjuntos[act.id] || [];
            const adjuntosCount = adjuntosCounts[act.id] ?? adjuntos.length;
            
            return (
              <div key={act.id} className="border rounded-lg overflow-hidden">
                <div 
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleActuacion(act.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        {act.titulo}
                        <ActuacionIABadges act={act} />
                        {(firmasExp.porActuacion[act.id] || []).map((f) => (
                          <FirmaBadge key={f.id} firma={f} onRenovado={firmasExp.reload} />
                        ))}
                        {adjuntosCount > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            <Paperclip className="w-3 h-3 mr-1" />
                            {adjuntosCount}
                          </Badge>
                        )}
                      </h4>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(act.fecha).toLocaleDateString('es-ES')}
                        </span>
                        {act.duracion && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {act.duracion}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Botones de editar y eliminar */}
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={(e) => handleEditClick(act, e)}
                        title="Editar actuación"
                      >
                        <Pencil className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={(e) => handleDeleteClick(act, e)}
                        title="Eliminar actuación"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-gray-50">
                    {(act.observaciones || act.descripcion) && (
                      <div className="mt-3" data-color-mode="light">
                        <p className="text-sm font-medium text-gray-700 mb-2">Observaciones:</p>
                        <div className="bg-white p-3 rounded border prose prose-sm max-w-none">
                          <MDEditor.Markdown source={act.observaciones || act.descripcion} />
                        </div>
                      </div>
                    )}
                    
                    <ActuacionAdjuntos 
                      actuacionId={act.id}
                      firmasPorAdjunto={firmasExp.porAdjunto}
                      onFirmasChange={firmasExp.reload}
                      adjuntos={adjuntos}
                      onUpdate={() => { loadAdjuntosActuaciones(); loadAdjuntosDetalle(act.id); }}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActuacionesSection;
